import { useState } from "react";
import { ApiError } from "../../api/client";
import { useCrewStatus, useDeferVisit, type CrewSite } from "../../api/crew";
import { formatDateTime } from "../../api/dates";

/** SITE STATUS: the moves the workflow allows from here, as big buttons,
 * and "Couldn't do it today…" for a visit that did not go ahead. The first
 * move is the usual one and is drawn solid; a move that cannot be sent is
 * kept on the phone and says so. A deferral needs a signal: it is not a
 * status move, and the offline queue only replays those. */
export function SiteStatusCard({ site }: { site: CrewSite }) {
  const move = useCrewStatus(site.id);
  const [note, setNote] = useState("");
  const [queuedMove, setQueuedMove] = useState<string | null>(null);
  const [deferring, setDeferring] = useState(false);

  return (
    <section className="crew-card" aria-label="Site status">
      <h2 className="section-label">Site status</h2>
      {site.deferred ? (
        <p className="notice warn">
          Not done on {formatDateTime(site.deferred.at)}: {site.deferred.reason}
          {site.deferred.note ? ` (${site.deferred.note})` : ""}.
        </p>
      ) : null}
      {site.allowed_next.length === 0 ? (
        <p className="crew-note">Nothing follows {site.status_label.toLowerCase()}; the work on this site is closed.</p>
      ) : (
        <>
          {/* Folded, because most moves carry no note -- but above the
              buttons, because a note has to be written before the tap. */}
          <details className="move-note" open={note !== ""}>
            <summary>Add a note to the move</summary>
            <label className="field crew-field">
              Note (optional)
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          </details>
          <div className="status-moves">
            {site.allowed_next.map((next, index) => (
              <button
                key={next.value}
                type="button"
                className={index === 0 ? "crew-btn crew-btn-go" : "crew-btn crew-btn-outline"}
                disabled={move.isPending}
                onClick={() =>
                  move.mutate(
                    { new_status: next.value, note: note.trim() || null },
                    {
                      onSuccess: (r) => {
                        setNote("");
                        setQueuedMove(r.queued ? next.label : null);
                      },
                    },
                  )
                }
              >
                {next.label}
              </button>
            ))}
          </div>
        </>
      )}
      {queuedMove ? (
        <p className="notice warn">No signal: “{queuedMove}” is saved on the phone and sends when there is signal.</p>
      ) : null}
      {move.isError ? <p className="notice bad">{move.error.message}</p> : null}
      {site.allowed_next.length > 0 ? (
        deferring ? (
          <DeferForm site={site} onDone={() => setDeferring(false)} />
        ) : (
          <button type="button" className="crew-btn crew-btn-defer" onClick={() => setDeferring(true)}>
            Couldn't do it today…
          </button>
        )
      ) : null}
    </section>
  );
}

function DeferForm({ site, onDone }: { site: CrewSite; onDone: () => void }) {
  const defer = useDeferVisit(site.id);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const chosen = site.deferral_reasons.find((r) => r.code === reason);
  const ready = chosen !== undefined && (!chosen.needs_note || note.trim() !== "");
  // A lost signal is a failed fetch or a gateway error, not the server's
  // refusal; only a refusal has a sentence worth showing.
  const offline = defer.isError && !(defer.error instanceof ApiError && defer.error.status < 500);
  return (
    <form
      className="defer-form"
      onSubmit={(e) => {
        e.preventDefault();
        defer.mutate({ reason, note: note.trim() || null }, { onSuccess: onDone });
      }}
    >
      <fieldset className="defer-reasons">
        <legend>Why couldn't it go ahead?</legend>
        {site.deferral_reasons.map((r) => (
          <label key={r.code} className="defer-reason">
            <input type="radio" name="reason" value={r.code} checked={reason === r.code} onChange={() => setReason(r.code)} />
            {r.label}
          </label>
        ))}
      </fieldset>
      <label className="field crew-field">
        {chosen?.needs_note ? "What happened (required)" : "What happened (optional)"}
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {defer.isError ? (
        <p className="notice bad">
          {offline
            ? "This needs a signal. Try again when you have one; the site keeps its status meanwhile."
            : defer.error.message}
        </p>
      ) : null}
      <div className="defer-actions">
        <button type="button" className="crew-btn crew-btn-outline" onClick={onDone}>
          Cancel
        </button>
        <button type="submit" className="crew-btn crew-btn-go" disabled={!ready || defer.isPending}>
          Record it
        </button>
      </div>
    </form>
  );
}
